/**
 * Every persuasive or explanatory sentence in the campaign experience, keyed by
 * audience — PRD §11.
 *
 * WHY ONE FILE
 * ------------
 * The PRD delivers ONE journey as THREE campaign destinations, and §1 forbids a
 * selector or any link between them: a journalist ad opens only the journalist
 * experience. The route fixes the voice. Three parallel component trees would
 * drift within a week, and a drifted variant is invisible — nobody sees more
 * than one of them.
 *
 * So the components are shared and the words live here. Adding a screen means
 * adding a key to all three columns, and a missing key is a loud `undefined` in
 * development rather than a silently English-for-journalists page.
 *
 * SHARED SYSTEM LABELS STAY SHARED (§11): Skip, Back to opportunities, Loading,
 * Retry, Privacy, Terms. They are in SHARED below, not in the per-audience map.
 *
 * VOICE (brand book §9): precise, analytical, measured. No marketing
 * exaggeration, no buzzwords, no emotional language. This is also why PRD §2
 * bans "perfectly matches your brief" style reasons on the opportunity rows.
 *
 * Typographic apostrophes (’) are deliberate — they are what the approved copy
 * and the review mockups use.
 */

/** The three campaign destinations. The route picks one; nothing else may. */
export const AUDIENCES = ['journalist', 'publisher', 'marketing'];

export const DEFAULT_AUDIENCE = 'journalist';

export function isAudience(value) {
  return AUDIENCES.includes(String(value || ''));
}

/**
 * §1 — attribution parameters describe the campaign but never decide which copy
 * appears. A route with missing or junk parameters still renders its configured
 * variant, so this falls back to the route's own audience, never to a parameter.
 */
export function normalizeAudience(value) {
  return isAudience(value) ? String(value) : DEFAULT_AUDIENCE;
}

/**
 * The cap on every free-text box in the funnel, and the number the counter
 * counts to. 1000, not the backend's 2000: the server truncates silently
 * at its own limit, and a brief that is quietly cut in half is worse than
 * one that stops where the writer can see it stop.
 */
export const FREE_TEXT_MAX = 1000;

export const SHARED = {
  skip: 'Skip',
  next: 'Next',
  back: 'Back',
  backToOpportunities: 'Back to opportunities',
  backToMyOpportunities: 'Back to my opportunities',
  retry: 'Retry',
  loading: 'Loading…',
  other: 'Other',
  otherFieldLabel: 'Tell us more',
  emailLabel: 'Email address',
  // §4 — the same button on every route; only the line above it changes.
  getMyOpportunities: 'Get my opportunities',
  emailPlaceholder: 'you@example.com',
  faq: 'FAQ',
  pricing: 'Pricing',
  requestADemo: 'Request a demo',
  requestAGuidedDemo: 'Request a guided demo',
  exploreAlchemiq: 'Explore Alchemiq',
  openResearch: 'Open research',
  openList: 'Open your list',
  seePreview: 'See preview',

  // What the panel says while it is working. A spinner alone is a shape;
  // these say which job is turning, which matters because the same panel
  // serves the list search and the research run. §2 still applies — no
  // duration, no percentage, nothing that could turn out to be false.
  // The card whose research is already running. It is a WAY BACK IN, not a
  // status: `requestReport` returns the existing report before it looks at
  // the allowance, so re-opening the one in flight costs nothing and lands
  // on its wait screen. The arrow is this site's mark for "takes you
  // somewhere", the same as "See preview →".
  cardPreparing: 'Preparing… →',
  // What the registration button says is missing when someone presses it
  // before it is ready. Named rather than generic: "something is wrong"
  // sends people hunting, and the box is easy to miss under two fields.
  consentRequired: 'Please tick the box above so we can email you.',
  emailRequired: 'Please add your email address.',
  emailAndConsentRequired: 'Please add your email address and tick the box above.',
  // The footnote under it, split so the two facts do not run together.
  enrollFootnote: [
    'Up to five research reports in total, including the first.',
    'You can stop emails at any time.',
  ],
  panelFindingList: 'Please wait while we find your opportunities.',
  panelPreparingReport: 'Please wait while we prepare your research.',

  // §2 — an empty brief asks for a description and keeps focus in the field. It
  // never starts a job, and it never demands a company name.
  emptyBriefError: 'Please describe what you are looking for in a sentence or two.',

  // §2/§8 — a technical failure. Deliberately NOT an automatic quality retry:
  // the visitor asks for the retry, and their brief and answers are still here.
  firstListFailed:
    'Something went wrong while preparing your list. Your brief is saved — you can try again.',

  // §2 — zero useful results is a truthful outcome, not an error. Offer a way to
  // revise rather than padding the list with weak matches.
  firstListEmpty:
    'We did not find opportunities matching this brief in the past day. Try describing what you '
    + 'are looking for a little differently.',
  reviseBrief: 'Revise my brief',

  // The technical failure. A system message, so it is shared (§11 keeps Skip,
  // Retry and Loading shared for the same reason).
  firstListFailedTitle: 'We could not finish your list.',
  firstListEmptySub: 'The search finished — nothing in the past day fitted.',

  reportFailed: 'We could not prepare this research. You can try again — this did not use up one '
    + 'of your reports.',

  // Approved 30 Sep: there is no report-recovery email. Q1 removes every
  // message except the curated list and two setup reminders, so there is
  // nothing to offer an address for at this point in the journey.

  // ── Q3: the wait, past the point where it is still just slow ───────
  //
  // Two minutes is an expectation, not a promise. Past five it is a problem,
  // and the screen has to say so rather than keep spinning — a spinner that
  // never resolves is the same untruth as a fake percentage.
  listDelayed: 'Your list is taking longer than expected.',
  tryAgain: 'Try again',

  // Third failure. No retry offered: two have already not worked, and the
  // three of us have been told.
  listFailedFinalTitle: 'Uh-oh, something went wrong',
  listFailedFinalBody:
    'We couldn’t prepare your list. Please come back and try again later.',

  // ── Q14/Q17: the two upgrade prompts ───────────────────────────────
  upgradeAction: 'Upgrade your account',
  notNow: 'Not now',
  allowanceTitle: 'Create additional reports',
  allowanceBody: 'To create additional reports, upgrade your account.',
  expiredTitle: 'Keep discovering',
  expiredBody:
    'Upgrade your account to continue receiving weekday opportunities and creating reports.',
};

/**
 * Q11 — the two FAQ entries every route carries, after its own four.
 *
 * "Who uses Alchemiq?" is the same claim whoever is reading it, and the price
 * is the price.
 */
/**
 * Copy lifted VERBATIM from the public marketing site, alchemiq.ai.
 *
 * Kept in one block, and deliberately not paraphrased. Everything the
 * campaign says to a visitor is Or's to approve; this is the one place the
 * campaign speaks in words that came from somewhere else, so it should be
 * obvious at a glance which words those are and where to check them.
 *
 * Sourced 30 September 2026 from the site's closing CTA band, its results
 * section and its footer. If the site is rewritten, this goes stale silently
 * — there is no feed between the two.
 */
export const SITE = {
  origin: 'https://alchemiq.ai',

  // The site's closing band, reproduced as the campaign's CTA strip.
  ctaTitle: 'Discover what your audience will care about next.',
  ctaBody:
    'See how Alchemiq turns real-time signals into relevant content '
    + 'opportunities for your organization.',
  ctaPrimary: 'Request a demo',
  ctaSecondary: 'See customer results',
  ctaSecondaryHref: 'https://alchemiq.ai/case-studies',

  resultsEyebrow: 'Proven results',
  resultsTitle: 'Discovery that produces measurable results',
  results: [
    {
      figure: '+83%',
      label: 'Pageviews growth',
      body: 'Average increase in sections publishing Alchemiq content.',
    },
    {
      figure: '+23%',
      label: 'Organic search',
      body: "Increase in overall organic search to a reference client's entire website.",
    },
    {
      figure: 'X10',
      label: 'Growth multiplier',
      body: 'Increase in pageviews attributed to Alchemiq content over 10 months.',
    },
  ],

  footerBlurb:
    'Alchemiq is the intelligence layer for content. It discovers emerging '
    + 'opportunities, identifies what each audience is likely to care about next, '
    + 'and turns real-time intelligence into publishable content.',
  footerLinks: [
    { label: 'Why Alchemiq', href: 'https://alchemiq.ai/#why' },
    { label: 'Results', href: 'https://alchemiq.ai/#results' },
    { label: 'Case Studies', href: 'https://alchemiq.ai/case-studies' },
    { label: 'For Content Teams', href: 'https://alchemiq.ai/solutions/pr' },
    { label: 'Contact', href: 'https://alchemiq.ai/contact' },
  ],
  supportEmail: 'support@alchemiq.ai',
  social: [
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/alchemiq-ai/' },
    { label: 'YouTube', href: 'https://www.youtube.com/@AlchemiqAI' },
  ],
  copyright: '\u00A9 2026 Alchemiq. All rights reserved. Powered by AI. Guided by humans.',
  legal: [
    { label: 'Privacy', href: 'https://alchemiq.ai/privacy' },
    { label: 'Terms', href: 'https://alchemiq.ai/terms' },
  ],
};

export const SHARED_FAQ = [
  {
    question: 'Who uses Alchemiq?',
    answer:
      'Some of the biggest newsrooms in Europe, research firms, PR agencies and content '
      + 'marketing teams use Alchemiq to discover relevant stories and content opportunities.',
  },
  {
    question: 'How much does it cost?',
    answer:
      'You can get started at no cost. Upgrade your account for $99/month to continue '
      + 'receiving weekday opportunities and creating reports. For the full platform and '
      + 'services tailored to your team, request a guided demo.',
    demoCta: true,
  },
];

/**
 * Q12 — the Pricing page.
 *
 * $99/month replaces the $299 working assumption. The upgrade destination is
 * deliberately deferred, so the button exists and goes nowhere yet; that is a
 * product decision, not an oversight, and it is why there is no card form.
 */
export const PRICING = {
  title: 'Keep discovering',
  lede: 'Get started at no cost with opportunities matched to your audience and sourced '
    + 'research reports.',
  price: '$99/month',
  priceBody: 'Upgrade your account to receive curated opportunities every weekday and create '
    + 'additional reports.',
  platformTitle: 'Explore the full Alchemiq platform',
  platformBody:
    'Discover the full research platform, real-time alerts about stories about to trend, '
    + 'story discovery directly in ChatGPT, Claude or Gemini, sentiment analysis, '
    + 'topic-specific media coverage analysis, publishing-system integration, and customized '
    + 'formats and style guides.',
};

/** The guided-demo form. One form, reachable from everywhere (Q5). */
export const DEMO_FORM = {
  title: 'Discover what Alchemiq can do for your team',
  lede: 'Tell us a little about your work, and we’ll show you the relevant capabilities.',
  name: 'Name',
  email: 'Work email',
  organization: 'Organization',
  interest: 'What would you like to explore?',
  submit: 'Request a guided demo',
  confirmation: 'Thanks! We’ll be in touch to arrange your demo.',
};

/**
 * Q9 — one subject format for every audience and every send, weekday or
 * Friday: `[Weekday] opportunities: [highest-ranked story headline]`.
 *
 * This replaced three topic-based subjects. The headline carries the interest;
 * a subject that says "Today's opportunities" and nothing else tells a reader
 * nothing they did not already know.
 */
export function buildCuratedEmailSubject(weekdayName, topHeadline) {
  const day = String(weekdayName || '').trim();
  const headline = String(topHeadline || '').trim();
  if (!day) return headline || 'Opportunities from Alchemiq';
  return headline ? `${day} opportunities: ${headline}` : `${day} opportunities`;
}

/**
 * §3 — the three capability panels shown during the report wait.
 */
export const CAPABILITY_PANELS = ['deeperDiscovery', 'fitYourWorkflow', 'moreWaysToWork'];

/**
 * What "See preview" actually shows. One entry per panel, shared by both
 * designs and by every audience: the capability copy differs per audience,
 * the product does not.
 *
 * `illustrative` is load-bearing, not decoration. §3 forbids presenting a
 * reconstructed interface as a product screenshot, and until 6 Oct 2026 no
 * approved captures existed, so every preview carried the disclaimer. Two of
 * these are now real captures — the platform and the MCP connector in
 * ChatGPT — and leaving the disclaimer on them would be the same rule broken
 * from the other side: telling someone a genuine screenshot is a sketch.
 *
 * The signals preview keeps it, because it previews a version that is not
 * released; the image says so itself ("NEXT VERSION").
 *
 * `width`/`height` are the files' real pixel dimensions, and they are not
 * decoration either: the fold animates to the content's height, and a lazy
 * image that has not loaded has no height to animate to. With them the
 * browser reserves the box from the aspect ratio alone.
 */
export const CAPABILITY_PREVIEWS = Object.freeze({
  deeperDiscovery: {
    src: '/campaign/preview-research-platform.jpg',
    width: 1280,
    height: 614,
    alt: 'The Alchemiq research platform: a filtered feed of trending stories, '
      + 'each with its article count.',
    caption: 'The Alchemiq research platform.',
    illustrative: false,
  },
  fitYourWorkflow: {
    src: '/campaign/preview-mcp.jpg',
    width: 1023,
    height: 947,
    alt: 'ChatGPT answering with Alchemiq results: sourced story leads with dates, '
      + 'article counts and named publishers.',
    caption: 'Alchemiq inside ChatGPT, through the MCP connector.',
    illustrative: false,
  },
  moreWaysToWork: {
    src: '/campaign/preview-signals.jpg',
    width: 1280,
    height: 720,
    alt: 'A signals view: a story\u2019s source accumulation rising over time, '
      + 'beside a short note on why it matters now.',
    caption: 'Illustrative \u2014 not a screenshot of the product. A guided demo shows '
      + 'the real thing.',
    illustrative: true,
  },
});

const COPY = {
  journalist: {
    // ── Landing (§1) ──────────────────────────────────────────────────
    headline: 'Find stories your audience will care about.',
    support: 'Find timely stories beyond the sources you usually watch, across places and languages.',
    briefLabel: 'What kinds of stories are you looking for?',
    briefHelper: 'Tell us what you cover and who you write for in a sentence or two.',
    primaryAction: 'Find stories for me',
    // The landing aside. Its own copy, deliberately: reusing the list
    // title here promised "Stories worth a closer look" on a screen with
    // no list, which reads as a claim about something that does not exist
    // yet. This says what is about to happen instead.
    asideTitle: 'Discover stories worth pursuing',
    asideBody:
      'Describe your coverage and audience. Explore your first list here, then choose a story for deeper research.',
    // §1 — illustrative placeholder text INSIDE the box. Not buttons, not
    // templates: clicking one must not fill the field. They exist to show how
    // specific a useful brief is, and the field must still accept a broad one.
    briefExamples: [
      'e.g. I edit the culture section of a regional newspaper in Minnesota. I’m looking for arts, '
      + 'entertainment and cultural stories that would interest readers here.',
      'Or I’m a freelance science journalist covering health and technology for UK publications. '
      + 'I’m interested in new research with clear consequences for everyday life.',
    ],

    // ── First-list wait (§2, §11) ─────────────────────────────────────
    firstListWait: 'We’re finding stories for your coverage.',
    firstListWaitSupport:
      'Your list is being prepared from the brief you gave us. These questions are optional.',
    firstListReady: 'Your opportunities are ready',
    // A finished search that selected nothing. The waiting heading is not
    // true any more, and §2 requires the activity state to be truthful —
    // so the screen has to stop saying it is still looking.
    firstListEmptyTitle: 'No stories matched this brief.',

    // ── List (§11) ────────────────────────────────────────────────────
    listTitle: 'Stories worth a closer look',
    listSupport: 'Explore the angles and choose one for deeper research.',
    cardAction: 'Get research for a draft',

    // ── Report (§3, §11) ──────────────────────────────────────────────
    reportWait: 'We’re preparing your research.',
    reportReadyTitle: 'Your research is ready',

    // ── Invitation and enrollment (§4, §11) ───────────────────────────
    invitation: 'Get story opportunities beyond your usual sources.',
    enrollmentDetail:
      'Receive story opportunities each weekday for 14 days.\n\n'
      + 'Your first list and research are ready in your workspace.',

    // ── Workspace (§4, §11) ───────────────────────────────────────────
    workspaceTitle: 'Your story workspace',
    workspaceWiderPlatform:
      'This is one way Alchemiq supports discovery and reporting. Explore the full research '
      + 'platform with a guided demo.',

    // ── FAQ and Pricing (§4, §11) ─────────────────────────────────────
    faqIntro: 'How story discovery, research, and weekday delivery work.',
    pricingIntro: 'Continue receiving story opportunities and research after your campaign access.',


    // ── Guided demo (§4, §11) ─────────────────────────────────────────
    demoIntro: 'See how Alchemiq can support story discovery and research across your coverage.',
    demoConfirmation: 'Thanks. We’ll be in touch to arrange a story discovery walkthrough.',

    // ── Capability panels (§3) ────────────────────────────────────────
    capabilities: {
      deeperDiscovery: {
        title: 'Full research platform',
        body: 'Follow a developing story across places and languages in the full research platform.',
      },
      fitYourWorkflow: {
        title: 'MCP and integrations',
        body: 'Research can connect to editorial workflows through CMS integration.',
      },
      moreWaysToWork: {
        title: 'Alerts and media analysis',
        body: 'The wider platform offers tailored style guides and formats, plus MCP access.',
      },
    },

    // ── Optional questions during the first-list wait (§5) ────────────
    // Checkboxes, multi-select, every step skippable, and NEVER a prerequisite
    // for the list. The first run has already started from the brief and does
    // not restart when these are answered.
    // ── Q1: the one registration checkbox ──────────────────────────
    // One box, no time limit, and deliberately NOT followed by "I can
    // unsubscribe at any time" — the unsubscribe lives in the emails.
    registrationCheckbox: 'Email me curated story opportunities from Alchemiq.',

    // ── Q1/Q9: the curated email ───────────────────────────────────
    // `{topic}` is the feed's own subject, in the reader's words.
    curatedEmailOpening: 'Here are today’s story opportunities:',
    curatedEmailCta: 'Open your list',
    setupReminderSubject: 'Your next story could start here',
    setupReminderBody:
      'Discover story opportunities that speak to your audience’s interests.',
    setupReminderCta: 'Discover my opportunities',

    // ── Q11: the route's own four FAQ answers ──────────────────────
    faq: [
      {
        question: 'What will I get?',
        answer:
          'Story opportunities matched to your interests and audience, with a short '
          + 'explanation of why each fits. Choose an opportunity to create a sourced '
          + 'research report you can use to develop your story.',
      },
      {
        question: 'How are the opportunities selected?',
        answer:
          'Alchemiq’s relevance engine connects emerging stories across sources, languages '
          + 'and markets, identifying opportunities that matter to the audience you '
          + 'describe. It helps you discover relevant stories beyond your usual sources.',
      },
      {
        question: 'What’s in a research report?',
        answer:
          'A report brings together key facts, context and reporting from linked sources. '
          + 'You can copy it and use it as a foundation for developing your story.',
      },
      {
        question: 'How will I see the opportunities?',
        answer:
          'Your opportunities appear in your workspace. Register to receive curated lists '
          + 'by email.',
      },
      {
        question: 'Can I return to previous lists and reports?',
        answer:
          'Yes. Your workspace keeps your previous lists and reports, and the links in your '
          + 'emails take you back there.',
      },
      {
        question: 'What else can Alchemiq do?',
        answer:
          'Explore the full research platform, receive real-time alerts about stories about '
          + 'to trend, discover stories directly in ChatGPT, Claude or Gemini, and connect '
          + 'Alchemiq to your publishing system. Sentiment and topic-specific media coverage '
          + 'analysis are also available.\n\nRequest a guided demo to see how it can fit '
          + 'your workflow.',
        demoCta: true,
      },
    ],

    questions: [
      {
        key: 'usage',
        question: 'Where will you use these stories?',
        choices: [
          'News site', 'Newsletter', 'Social channels', 'Print', 'Video or TV',
          'Research or internal briefing', 'Other',
        ],
      },
      {
        key: 'selection',
        question: 'What helps you choose a story?',
        choices: [
          'Local relevance', 'A fresh angle', 'Strong evidence', 'Public impact',
          'A developing trend', 'Other',
        ],
      },
      {
        key: 'role',
        question: 'How do you usually work?',
        choices: ['Reporting', 'Editing', 'Producing', 'Researching', 'Other'],
      },
    ],
  },

  publisher: {
    headline: 'Grow your audience with better story discovery.',
    support: 'Spot stories with the potential to reach new readers and grow your audience.',
    briefLabel: 'What audiences and stories do you want to grow around?',
    briefHelper:
      'Tell us about your publication, the audience you want to grow, and the coverage that matters.',
    primaryAction: 'Find growth opportunities',
    // The landing aside. Its own copy, deliberately: reusing the list
    // title here promised "Stories worth a closer look" on a screen with
    // no list, which reads as a claim about something that does not exist
    // yet. This says what is about to happen instead.
    asideTitle: 'Find room to grow',
    asideBody:
      'Describe your publication and growth priority. Explore timely stories before choosing one for deeper research.',
    briefExamples: [
      'e.g. I lead a regional news publisher in the Midwest. We want stories with strong local '
      + 'relevance that can bring younger readers to our digital edition.',
      'Or our publication covers business and technology. We want overlooked stories that could '
      + 'grow newsletter readership.',
    ],

    firstListWait: 'We’re finding opportunities to grow your audience.',
    firstListWaitSupport:
      'Your list is being prepared from the brief you gave us. These questions are optional.',
    firstListReady: 'Your opportunities are ready',
    // A finished search that selected nothing. The waiting heading is not
    // true any more, and §2 requires the activity state to be truthful —
    // so the screen has to stop saying it is still looking.
    firstListEmptyTitle: 'No opportunities matched this brief.',

    listTitle: 'Opportunities for audience growth',
    listSupport: 'Explore timely stories that could bring readers to your publications.',
    cardAction: 'Get research for this opportunity',

    reportWait: 'We’re preparing the research behind this opportunity.',
    reportReadyTitle: 'Your opportunity research is ready',

    invitation: 'Grow your audience with better story discovery.',
    enrollmentDetail:
      'Receive growth opportunities each weekday for 14 days.\n\n'
      + 'Your first list and research are ready in your workspace.',

    workspaceTitle: 'Your growth opportunities',
    workspaceWiderPlatform:
      'This is one way Alchemiq supports audience growth. See the broader platform in a guided demo.',

    faqIntro: 'How growth opportunities, research, and weekday delivery work.',
    pricingIntro: 'Continue finding audience growth opportunities after your campaign access.',


    demoIntro: 'See how the wider platform can support audience growth and newsroom workflows.',
    demoConfirmation: 'Thanks. We’ll be in touch to arrange an audience growth walkthrough.',

    capabilities: {
      deeperDiscovery: {
        title: 'Full research platform',
        body: 'Explore emerging coverage opportunities across your publications and audiences.',
      },
      fitYourWorkflow: {
        title: 'MCP and integrations',
        body: 'Bring discovery into newsroom workflows through CMS integration.',
      },
      moreWaysToWork: {
        title: 'Alerts and media analysis',
        body: 'The wider platform offers team-level customization, formats, and MCP access.',
      },
    },

    registrationCheckbox: 'Email me curated story opportunities from Alchemiq.',

    curatedEmailOpening: 'Here are today’s story opportunities:',
    curatedEmailCta: 'Open your list',
    setupReminderSubject: 'Your next story could start here',
    setupReminderBody:
      'Discover story opportunities that speak to your audience’s interests.',
    setupReminderCta: 'Discover my opportunities',

    faq: [
      {
        question: 'What will I get?',
        answer:
          'Story opportunities matched to your publication’s audience and editorial focus, '
          + 'with a short explanation of why each fits. Your team can turn an opportunity '
          + 'into a sourced research report to help develop the story.',
      },
      {
        question: 'How are the opportunities selected?',
        answer:
          'Alchemiq’s relevance engine connects emerging stories across sources, languages '
          + 'and markets, identifying opportunities with strong potential to engage your '
          + 'publication’s audience. It helps your team discover relevant stories beyond its '
          + 'usual sources.',
      },
      {
        question: 'What’s in a research report?',
        answer:
          'A report brings together key facts, context and reporting from linked sources. '
          + 'Your team can copy it and use it as a foundation for developing the story.',
      },
      {
        question: 'How will I see the opportunities?',
        answer:
          'Your opportunities appear in your workspace. Register to receive curated lists '
          + 'by email.',
      },
      {
        question: 'Can I return to previous lists and reports?',
        answer:
          'Yes. Your workspace keeps your previous lists and reports, and the links in your '
          + 'emails take you back there.',
      },
      {
        question: 'What else can Alchemiq do?',
        answer:
          'Explore the full research platform, receive real-time alerts about stories about '
          + 'to trend, discover stories directly in ChatGPT, Claude or Gemini, and connect '
          + 'Alchemiq to your publishing system. Sentiment and topic-specific media coverage '
          + 'analysis are also available.\n\nRequest a guided demo to see how it can fit '
          + 'your newsroom’s workflow.',
        demoCta: true,
      },
    ],

    questions: [
      {
        key: 'usage',
        question: 'Where do you most want to grow?',
        // §5 — no "Research or internal briefing" here: a publisher growing an
        // audience is not growing an internal briefing.
        choices: ['News site', 'Newsletter', 'Social channels', 'Print', 'Video or TV', 'Other'],
      },
      {
        key: 'goal',
        question: 'Which growth goal matters most now?',
        choices: [
          'New readers', 'Returning readers', 'Subscribers', 'Younger audiences',
          // §5 calls out Niche audiences explicitly as a publisher growth goal.
          'Niche audiences', 'New markets', 'Other',
        ],
      },
      {
        key: 'help',
        question: 'What would help your team most?',
        choices: [
          'Earlier discovery', 'New coverage areas', 'Faster research', 'Consistent output', 'Other',
        ],
      },
    ],
  },

  marketing: {
    headline: 'Get ahead of the stories your audience will care about.',
    support: 'Find relevant developments early and turn them into useful research or content.',
    briefLabel: 'What opportunities should Alchemiq find for your team?',
    briefHelper: 'Tell us the subjects, markets, and audiences your team follows.',
    primaryAction: 'Find opportunities for my team',
    // The landing aside. Its own copy, deliberately: reusing the list
    // title here promised "Stories worth a closer look" on a screen with
    // no list, which reads as a claim about something that does not exist
    // yet. This says what is about to happen instead.
    asideTitle: 'Find what matters next',
    asideBody:
      'Describe the developments your team follows. Explore opportunities for research, communications or content.',
    briefExamples: [
      'e.g. I work in communications for a cybersecurity company. Find emerging developments that '
      + 'matter to IT leaders at mid-sized organizations.',
      'Or our content team covers sustainable travel for European consumers. We want timely '
      + 'research with practical relevance.',
    ],

    firstListWait: 'We’re finding developments relevant to your team.',
    firstListWaitSupport:
      'Your list is being prepared from the brief you gave us. These questions are optional.',
    firstListReady: 'Your opportunities are ready',
    // A finished search that selected nothing. The waiting heading is not
    // true any more, and §2 requires the activity state to be truthful —
    // so the screen has to stop saying it is still looking.
    firstListEmptyTitle: 'No opportunities matched this brief.',

    listTitle: 'Opportunities for your team',
    listSupport: 'Explore timely developments for research, communications, or content.',
    cardAction: 'Get research for this opportunity',

    reportWait: 'We’re preparing research for your team.',
    reportReadyTitle: 'Your team’s research is ready',

    invitation: 'Get ahead of the stories your audience will care about.',
    enrollmentDetail:
      'Receive relevant opportunities each weekday for 14 days.\n\n'
      + 'Your first list and research are ready in your workspace.',

    workspaceTitle: 'Your team’s opportunities',
    workspaceWiderPlatform:
      'This is one way Alchemiq supports monitoring and research. See the broader platform in a '
      + 'guided demo.',

    faqIntro: 'How monitoring, research, and weekday delivery work.',
    pricingIntro: 'Continue tracking relevant opportunities after your campaign access.',


    demoIntro: 'See how Alchemiq can support timely discovery and your content workflows.',
    demoConfirmation: 'Thanks. We’ll be in touch to arrange a walkthrough for your team.',

    capabilities: {
      deeperDiscovery: {
        title: 'Full research platform',
        body: 'Track emerging developments across markets and subjects relevant to your team.',
      },
      fitYourWorkflow: {
        title: 'MCP and integrations',
        body: 'Bring discovery into existing content workflows through available integrations.',
      },
      moreWaysToWork: {
        title: 'Alerts and media analysis',
        body: 'The wider platform offers style guides, formats, and MCP access.',
      },
    },

    // Q1 — "content", not "story": a PR or content team is not a newsroom.
    registrationCheckbox: 'Email me curated content opportunities from Alchemiq.',

    curatedEmailOpening: 'Here are today’s content opportunities:',
    curatedEmailCta: 'Open your list',
    setupReminderSubject: 'Your next content idea could start here',
    setupReminderBody:
      'Discover content opportunities that speak to your audience’s interests.',
    setupReminderCta: 'Discover my opportunities',

    faq: [
      {
        question: 'What will I get?',
        answer:
          'Content opportunities matched to your audience and the topics, industries or '
          + 'clients you specify, with a short explanation of why each fits. Turn an '
          + 'opportunity into a sourced research report to help develop a blog post, '
          + 'newsletter or social post.',
      },
      {
        question: 'How are the opportunities selected?',
        answer:
          'Alchemiq’s relevance engine connects emerging stories across sources, languages '
          + 'and markets, identifying opportunities with strong potential to engage your '
          + 'audience, matched to the topics, industries or clients you specify.',
      },
      {
        question: 'What’s in a research report?',
        answer:
          'A report brings together key facts, context and reporting from linked sources. '
          + 'You can copy it and use it as a foundation for developing content for your '
          + 'brand or client.',
      },
      {
        question: 'How will I see the opportunities?',
        answer:
          'Your opportunities appear in your workspace. Register to receive curated lists '
          + 'by email.',
      },
      {
        question: 'Can I return to previous lists and reports?',
        answer:
          'Yes. Your workspace keeps your previous lists and reports, and the links in your '
          + 'emails take you back there.',
      },
      {
        question: 'What else can Alchemiq do?',
        answer:
          'Analyze sentiment and media coverage of the topics, industries or clients you '
          + 'follow, receive real-time alerts about stories about to trend, and discover '
          + 'content opportunities directly in ChatGPT, Claude or Gemini.\n\nRequest a '
          + 'guided demo to explore the full research platform and how it can fit your '
          + 'team’s workflow.',
        demoCta: true,
      },
    ],

    questions: [
      {
        key: 'usage',
        question: 'Where will you use these opportunities?',
        choices: [
          'Company site', 'Newsletter', 'Social channels', 'Client channels', 'Video',
          'Internal research', 'Internal communications', 'Other',
        ],
      },
      {
        key: 'intent',
        question: 'What are you looking to do?',
        choices: [
          'Monitor a topic', 'Brief colleagues', 'Find content ideas', 'Respond to a development',
          'Support client work', 'Other',
        ],
      },
      {
        key: 'value',
        question: 'What makes an opportunity useful?',
        choices: [
          'Early signal', 'Credible evidence', 'Audience relevance', 'A distinct angle',
          'Market relevance', 'Other',
        ],
      },
    ],
  },
};

/**
 * The whole copy block for one audience. Throws on an unknown audience rather
 * than falling back: a silent fallback is how a publisher ends up reading
 * journalist copy on a live ad, and the route is the only thing that sets this.
 */
export function copyFor(audience) {
  const block = COPY[audience];
  if (!block) throw new Error(`Unknown campaign audience: ${audience}`);
  return block;
}

export default COPY;
